import {
  GridReadyEvent,
  IServerSideDatasource,
  RowDoubleClickedEvent,
} from "ag-grid-community";
import React, {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
} from "react";
import { dateFilterModel } from "../../../../utils/Formatter/AgGridFilter";
import { createGraphQLClientWithMiddleware } from "../../../../services/graphqlClient";
import { GOODS_ISSUE_VIEW } from "../../../../services/AgGrid/InventoryAggrid";
import { AgGridReact } from "ag-grid-react";
import { IGoodsIssue } from "../../../../types/Inventory/GoodsIssue";
import { useGoodsIssueColumnDefs } from "./columnDef";
import { useNavigate } from "react-router-dom";
import AgGrid from "../../../UI/AgGrid";
import { branchInfoFromSessionStorage } from "../../../../utils/Global";

interface Props {
  gridRef: RefObject<AgGridReact<IGoodsIssue>>;
  isFilter?: string | null;
  referenceUniqueId?: string;
  setRowCount?: Dispatch<SetStateAction<number>>;
}
const GoodsIssueTable = ({
  gridRef,
  referenceUniqueId,
  isFilter,
  setRowCount,
}: Props) => {
  const navigate = useNavigate();
  const columnDef = useGoodsIssueColumnDefs(referenceUniqueId);
  const graphQLClient = createGraphQLClientWithMiddleware("wms");
  const datasource: IServerSideDatasource = {
    async getRows(params) {
      const { request } = params;
      const { startRow, endRow, filterModel, sortModel } = request;

      const { created_date, posted_date } = filterModel;

      const formatFilter = {
        ...filterModel,
        created_date: dateFilterModel(created_date),
        posted_date: dateFilterModel(posted_date),
      };

      try {
        const { GoodsIssuesView } = await graphQLClient.request(
          GOODS_ISSUE_VIEW,
          {
            aggridInput: {
              startRow,
              endRow,
              filterModel: formatFilter,
              sortModel,
            },
          }
        );

        if (setRowCount) {
          setRowCount(GoodsIssuesView.count as number);
        }

        params.success({
          rowData: GoodsIssuesView.results as any[],
          rowCount: GoodsIssuesView.count as number,
        });
      } catch (err) {
        params.fail();
      }
    },
  };

  const onFilterChanged = useCallback(
    (params: any) => {
      const instance = params.api.getFilterInstance("aggrid_status");
      const reference = params.api.getFilterInstance("reference_unique_id");
      switch (isFilter) {
        case "draft":
          instance?.setModel({ values: ["draft"] });
          break;
        case "wait_approve":
          instance?.setModel({ values: ["wait_approve"] });
          break;
        case "not_approved":
          instance?.setModel({ values: ["not_approved"] });
          break;
        case "wait_issue":
          instance?.setModel({ values: ["wait_issue"] });
          break;
        case "finished":
          instance?.setModel({ values: ["finished"] });
          break;
        case "cancelled":
          instance?.setModel({ values: ["cancelled"] });
          break;
        default:
          instance?.setModel({
            values: [
              "draft",
              "wait_approve",
              "not_approved",
              "wait_issue",
              "finished",
            ],
          });
          break;
      }
      reference?.setModel({
        filterType: "text",
        type: "contains",
        filter: referenceUniqueId,
      });
      params.api.onFilterChanged();
    },
    [isFilter, referenceUniqueId]
  );

  const onGridReady = (params: GridReadyEvent) => {
    const { branch_id, is_hq } = branchInfoFromSessionStorage();

    const instanceฺฺBranchId = params.api.getFilterInstance("branch_id");
    instanceฺฺBranchId?.setModel({
      filterType: "number",
      type: "equals",
      filter: is_hq === "false" && branch_id ? parseInt(branch_id) : undefined,
    });
    onFilterChanged(params);
    params.api.setServerSideDatasource(datasource);
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      onFilterChanged(gridRef.current);
    }
  }, [gridRef, onFilterChanged]);

  const onRowDoubleClicked = (params: RowDoubleClickedEvent) => {
    const { id } = params.data;
    navigate(`/inventory/goods-issue/${id}`);
  };

  return (
    <AgGrid
      ref={gridRef}
      columnDefs={columnDef}
      height={665}
      onRowDoubleClicked={onRowDoubleClicked}
      onGridReady={onGridReady}
      path={"/inventory/goods-issue"}
    />
  );
};

export default GoodsIssueTable;
